<template>
  <div id="side-menu" :class="{ hidden: !showMenu }">
    <div class="pc" :class="expand ? 'expand' : 'shrink'">
      <div class="back-home">
        <router-link :to="{ name: 'home' }" :class="{ expand }" class="tutorial-back-home">
          <span v-if="expand">{{ $t("sideMenu.backHome") }}</span>
          <img v-else src="@/assets/img/icon/house.svg" alt="home" :width="30" />
        </router-link>
      </div>
      <div class="tutorial-novel-top">
        <side-menu-novel :novel-id="novelId" :expand="expand" />
      </div>
      <div class="link-list tutorial-novel-bottom">
        <side-menu-link-list :novel-id="novelId" :expand="expand" />
      </div>
      <div v-if="expand && canDisplayBannerResult" :key="bannerKey" class="agent-feature-promotion-banner">
        <AgentFeaturePromotionBanner :detail="detail" @banner-click="openAgentFeaturePromotionDialog" />
      </div>
      <button class="btn-expand" @click="onClickExpand">
        <img v-if="expand" src="@/assets/img/static/sidemenu-close.png" />
        <img v-else src="@/assets/img/static/sidemenu-open.png" />
      </button>
    </div>

    <div class="sp">
      <side-menu-link-sp :novelId="novelId" link="theme" :label="$t('sideMenu.sp.theme')">
        <img class="sp-icon" src="@/assets/img/menu-icon/theme.png" />
      </side-menu-link-sp>
      <side-menu-link-sp :novelId="novelId" link="plot" :label="$t('sideMenu.sp.plot')">
        <img class="sp-icon" src="@/assets/img/menu-icon/account_tree.svg" />
      </side-menu-link-sp>
      <side-menu-link-sp :novelId="novelId" link="editor" :label="$t('sideMenu.sp.writing')">
        <img class="sp-icon" src="@/assets/img/menu-icon/edit.png" />
      </side-menu-link-sp>
      <side-menu-link-sp :novelId="novelId" link="characters" :label="$t('sideMenu.sp.settings')">
        <img class="sp-icon" src="@/assets/img/menu-icon/character.png" />
      </side-menu-link-sp>
      <side-menu-link-sp :novelId="novelId" link="detail" :label="$t('sideMenu.sp.book')">
        <img class="sp-icon" src="@/assets/img/menu-icon/setting.png" />
      </side-menu-link-sp>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import SideMenuLinkSp from "@/components/atoms/SideMenuLinkSP.vue";
import SideMenuLinkList from "@/components/organisms/lists/SideMenuLinkList.vue";
import SideMenuNovel from "@/components/molecules/SideMenuNovel.vue";
import AgentFeaturePromotionBanner from "@/components/molecules/AgentFeaturePromotionBanner.vue";
import AgentFeaturePromotionDialog from "@/components/ui/AgentFeaturePromotionDialog.vue";
import { Route } from "vue-router";
import { Dialog } from "@/lib/utils";
import { useAgentStore, AgentStore } from "@/stores/agent";
import { User } from "@/lib/models";
import axiosBase from "axios";
import { showNotifyDialog } from "@/lib/dialog";
import { NolaAnalytics, NolaItemId, NolaContentType } from "@/lib/utils/analytics";

interface Data {
  expand: boolean;
  showMenu: boolean;
  agentStore: AgentStore;
  detail: {
    imageUrl: string;
    text: string;
    promoBannerWordCount: number | null;
  };
  bannerKey: number;
  canDisplayBannerResult: boolean;
  hasLoggedViewEvent: boolean;
}

interface Computed {
  expandsSideMenu: boolean;
  user: User;
  isAgentFeatureNotAgreed: boolean;
  novelContentLength: number;
  isContentLengthOverThreshold: boolean;
  hasNotUsedAgentFeaturePromoBanner: boolean;
  hasDisplayedAgentFeaturePromoBanner: boolean;
  isManuscriptEmpty: boolean;
  isManuscriptUnsavedAgentNavigation: boolean;
  hasShownWordLimitErrorDialog: boolean;
}

interface Methods {
  onClickExpand(): void;
  openAgentFeaturePromotionDialog(): void;
  fetchAgentFeatureDetails(): void;
  evaluateCanDisplayBanner(): void;
  updateCanDisplayBanner(): void;
  sendBannerViewEvent(): void;
}

interface Props {
  novelId: string;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { SideMenuLinkList, SideMenuLinkSp, SideMenuNovel, AgentFeaturePromotionBanner },

  async beforeCreate() {
    // プロモーション風バナーの表示制御に利用する原稿保存状態フラグを初期化
    this.$store.commit("manuscriptModule/setManuscriptSaved", true);
    // プロモーション風バナーを作品毎に表示制御するため表示済みフラグを初期化する
    const agentStore = useAgentStore();
    agentStore.setDisplayedAgentFeaturePromoBanner(false);
  },

  async created() {
    // 最新の作品情報をstateに格納する
    const { $store, novelId } = this;
    await $store.dispatch("novelModule/initialize");
    $store.dispatch("novelModule/fetchNovel", { novelId });
    $store.dispatch("novelModule/initializeExpandsSideMenu");

    // プロモーション風バナーのデータをmicroCMSから取得
    this.fetchAgentFeatureDetails();
    // プロモーション風バナーの表示条件を評価
    this.canDisplayBannerResult = await this.evaluateCanDisplayBanner();

    // 初回表示時にバナーが表示される場合のイベント送信
    this.sendBannerViewEvent();
  },

  props: {
    novelId: { type: String, required: true },
  },

  data() {
    return {
      expand: true,
      showMenu: true,
      agentStore: useAgentStore(),
      detail: {
        imageUrl: "",
        text: "",
        promoBannerWordCount: null,
      },
      canDisplayBannerResult: false,
      bannerKey: 0,
      hasLoggedViewEvent: false,
    };
  },

  watch: {
    $route(newRoute: Route, oldRoute: Route) {
      if (newRoute.name === "editor" && newRoute.params.manuscriptKey != null) {
        this.showMenu = false;
        return;
      }

      this.showMenu = true;
    },
    expandsSideMenu: {
      handler(expandsSideMenu: boolean) {
        this.expand = expandsSideMenu;
      },
      immediate: true,
    },
    novelContentLength() {
      this.updateCanDisplayBanner();
    },
    isManuscriptUnsavedAgentNavigation() {
      this.updateCanDisplayBanner();
    },
    hasShownWordLimitErrorDialog() {
      // 10万字エラーダイアログが表示された瞬間のみ
      // プロモーションバナー再表示のため、バナーキーを更新
      if (this.hasShownWordLimitErrorDialog && this.canDisplayBannerResult) {
        this.bannerKey += 1;
      }
    },
  },

  computed: {
    expandsSideMenu() {
      return this.$store.getters["novelModule/expandsSideMenu"];
    },
    user() {
      return this.$store.getters["userModule/user"];
    },
    isAgentFeatureNotAgreed() {
      return this.user.agentStartedAt === null;
    },
    novelContentLength() {
      return this.$store.getters["novelModule/novelContentLength"];
    },
    isContentLengthOverThreshold() {
      return this.novelContentLength >= this.detail.promoBannerWordCount;
    },
    hasNotUsedAgentFeaturePromoBanner() {
      return !this.$store.getters["generalModule/hasUsedAgentFeaturePromoBanner"];
    },
    hasDisplayedAgentFeaturePromoBanner() {
      return this.agentStore.hasDisplayedAgentFeaturePromoBanner;
    },
    isManuscriptEmpty() {
      return this.$store.getters["manuscriptModule/manuscriptList"].length === 0;
    },
    isManuscriptUnsavedAgentNavigation() {
      return this.agentStore.isManuscriptUnsavedAgentNavigation;
    },
    hasShownWordLimitErrorDialog() {
      return this.$store.getters["manuscriptModule/hasShownWordLimitErrorDialog"];
    },
  },

  methods: {
    onClickExpand() {
      this.$store.dispatch("novelModule/toggleSideMenu");
    },
    openAgentFeaturePromotionDialog() {
      const agentFeaturePromotionDialog = new Dialog(AgentFeaturePromotionDialog as any);
      agentFeaturePromotionDialog.show();
    },
    async fetchAgentFeatureDetails() {
      try {
        const axios = axiosBase.create({
          baseURL: process.env.VUE_APP_MICROCMS_API_ENDPOINT,
          headers: { "X-API-KEY": process.env.VUE_APP_MICROCMS_API_KEY },
        });

        const detailRequest = await axios.get(`/agent_feature_detail`);
        if (detailRequest.status !== 200) {
          throw new Error("Failed to fetch data");
        }

        const [content] = detailRequest.data.contents;
        this.detail.imageUrl = content.promoBannerImage.url;
        this.detail.text = content.promoBannerText;
        this.detail.promoBannerWordCount = content.promoBannerWordCount;
      } catch (error) {
        await showNotifyDialog({
          title: this.$t("sideMenu.fetchError.title") as string,
          content: this.$t("sideMenu.fetchError.content") as string,
        });
        console.error(error);
      }
    },
    async evaluateCanDisplayBanner() {
      // バナー表示判定に利用している、novelContentLengthは他コンポーネントで
      // 更新されるため、Store反映に少し時間がかかる。
      // 反映される前にバナー表示判定を行うと、正しい判定ができないため、0.5秒待機する。
      await new Promise((resolve) => setTimeout(resolve, 500));

      if (this.isManuscriptEmpty) {
        // 原稿が存在しない場合はバナーの表示判定を行わず false を返す
        return false;
      }

      if (this.hasDisplayedAgentFeaturePromoBanner) {
        // 既にバナー表示済みの場合はバナー表示判定を行わず true を返す
        return true;
      }

      // バナーを表示可能な条件を全て満たす場合のみ true を返す
      const canDisplay =
        this.isAgentFeatureNotAgreed && this.isContentLengthOverThreshold && this.hasNotUsedAgentFeaturePromoBanner;

      if (canDisplay) {
        // バナー表示条件を満たす場合はバナー表示済みフラグを立てる
        this.agentStore.setDisplayedAgentFeaturePromoBanner(true);
      }

      return canDisplay;
    },
    async updateCanDisplayBanner() {
      this.canDisplayBannerResult = await this.evaluateCanDisplayBanner();

      // バナーが初回表示される場合のみview_itemイベントを送信
      this.sendBannerViewEvent();
    },
    sendBannerViewEvent() {
      // バナーが初回表示される場合のみview_itemイベントを送信
      if (this.canDisplayBannerResult && !this.hasLoggedViewEvent) {
        this.hasLoggedViewEvent = true;
        const nolaAnalytics = new NolaAnalytics(this.$gtm, this.$store);
        nolaAnalytics.logViewItemEvent(NolaItemId.AgentFeaturePromotionBanner, NolaContentType.Banner);
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

#side-menu {
  background-color: var(--color-surface-subtle);
  z-index: 10;

  @include mq(sp) {
    height: $sp-menu-height;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;

    &.hidden {
      display: none;
    }
  }
}

#side-menu > .pc.shrink {
  width: 60px;
  text-align: center;
}

.pc {
  display: flex;
  flex-direction: column;
  width: 230px;
  height: 100%;
  box-shadow: var(--shadow-md);
  transition: width 0.3s ease;

  @include mq(sp) {
    display: none;
  }
}

.back-home {
  box-sizing: border-box;
  padding: 5px 10px;
  background-color: var(--color-bg-dark);

  a {
    text-decoration: none;
    color: var(--color-text-on-fixed-bg);
    font-weight: var(--font-weight-bold);
    display: flex;
    justify-content: center;
    align-items: center;

    &.expand {
      justify-content: flex-start;
    }

    img {
      filter: brightness(0) invert(1);
    }
  }
}

.link-list {
  flex-grow: 1;
  overflow-y: auto;
}

.btn-expand {
  position: relative;
  @include hover-overlay;
  height: 50px;
  padding: 10px 15px;
  text-align: right;
  background-color: var(--color-surface-muted);
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;

}

.btn-expand img {
  height: 30px;
  width: 30px;
  filter: brightness(0);

  [data-app-theme="dark"] & {
    filter: brightness(0) invert(1);
  }
}

.sp {
  display: flex;
  justify-content: space-evenly;

  @include mq(pc) {
    display: none;
  }
}

.sp-sidemenu {
  width: 30px;
}

.sp-icon {
  margin-top: 10px;
  width: 23px;
  height: 23px;
  filter: brightness(0);

  [data-app-theme="dark"] & {
    filter: brightness(0) invert(1);
  }
}

.agent-feature-promotion-banner {
  display: flex;
  justify-content: center;
  margin: 10px 0;
}
</style>
